/* SEREIN QUEEN — PDP Fresh Mix V1
   Keeps product-page discovery fresh on every reload without losing relevance.
   Product World stays inside its assigned/source collection.
   More to Discover keeps the exact-category + complementary + trending balance.
   No polling or broad MutationObserver: performance-safe by design. */
(() => {
  'use strict';
  if (window.__SQ_PRODUCT_PAGE_FRESH_MIX_V1__) return;
  window.__SQ_PRODUCT_PAGE_FRESH_MIX_V1__ = true;

  const clean = value => String(value || '').trim();
  const randomIndex = length => {
    if (length <= 1) return 0;
    if (window.crypto?.getRandomValues) {
      const bucket = new Uint32Array(1);
      window.crypto.getRandomValues(bucket);
      return bucket[0] % length;
    }
    return Math.floor(Math.random() * length);
  };
  const shuffle = input => {
    const items = [...input];
    for (let index = items.length - 1; index > 0; index -= 1) {
      const swap = randomIndex(index + 1);
      [items[index], items[swap]] = [items[swap], items[index]];
    }
    return items;
  };
  const family = title => clean(title)
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .split(/\s+-\s+|\s+\/\s+|\||\(/)[0]
    .replace(/\b(red|blue|green|yellow|black|white|pink|purple|orange|gray|grey|brown|beige|navy|cream|gold|silver|small|medium|large|xs|sm|md|lg|xl|xxl|xxxl)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const storageGet = key => {
    try { return sessionStorage.getItem(key) || ''; } catch (_) { return ''; }
  };
  const storageSet = (key, value) => {
    try { sessionStorage.setItem(key, value); } catch (_) {}
  };
  const productKey = node => {
    if (!(node instanceof Element)) return '';
    const own = clean(node.dataset.productId || node.dataset.productHandle || node.dataset.handle);
    if (own) return own;
    const nested = node.querySelector('[data-product-id],[data-product-handle],[data-handle]');
    const nestedKey = clean(nested?.dataset.productId || nested?.dataset.productHandle || nested?.dataset.handle);
    if (nestedKey) return nestedKey;
    const href = node.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
    try { return decodeURIComponent(new URL(href, location.origin).pathname.match(/\/products\/([^/?#]+)/i)?.[1] || ''); }
    catch (_) { return clean(href); }
  };
  const titleFor = node => clean(
    node?.dataset?.title ||
    node?.querySelector?.('[data-product-title]')?.dataset?.productTitle ||
    node?.querySelector?.('.card__heading,.product-card__title,h3,h4')?.textContent ||
    node?.querySelector?.('a[href*="/products/"]')?.getAttribute('aria-label')
  );
  const signature = nodes => nodes.map(productKey).filter(Boolean).sort().join('|');
  const stripProductWorldControls = grid => grid?.querySelectorAll(
    '.sq-universal-bag,.sq-universal-card-trigger,[data-sq-universal-expanded-open],.sq-card-favorite-control,[data-sq-card-favorite-control],[data-sq-universal-favorite]'
  ).forEach(node => node.remove());

  function chooseFresh(pool, limit, previousSignature) {
    const available = pool.filter(node => productKey(node));
    if (available.length <= limit) return shuffle(available);

    let chosen = [];
    for (let attempt = 0; attempt < 7; attempt += 1) {
      const ids = new Set();
      const families = new Set();
      chosen = [];
      for (const node of shuffle(available)) {
        if (chosen.length >= limit) break;
        const key = productKey(node);
        const group = family(titleFor(node));
        if (!key || ids.has(key) || (group && families.has(group))) continue;
        ids.add(key);
        if (group) families.add(group);
        chosen.push(node);
      }
      if (chosen.length < limit) {
        for (const node of shuffle(available)) {
          if (chosen.length >= limit) break;
          const key = productKey(node);
          if (!key || ids.has(key)) continue;
          ids.add(key);
          chosen.push(node);
        }
      }
      if (!previousSignature || signature(chosen) !== previousSignature) break;
    }

    if (previousSignature && signature(chosen) === previousSignature) {
      const previous = new Set(previousSignature.split('|').filter(Boolean));
      const replacement = shuffle(available).find(node => !previous.has(productKey(node)) && !chosen.includes(node));
      if (replacement && chosen.length) chosen[chosen.length - 1] = replacement;
    }
    return shuffle(chosen).slice(0, limit);
  }

  function mixProductWorld(world) {
    if (!(world instanceof Element)) return;
    const grid = world.querySelector('[data-sq-product-world-grid]');
    if (!(grid instanceof HTMLElement)) return;
    const candidates = [...grid.children].filter(node => node instanceof HTMLElement);
    if (!candidates.length) return;

    const limit = Math.max(2, Number(world.dataset.limit || 4));
    const currentId = clean(world.dataset.productId);
    const key = `sq:pdp:fresh:product-world:${currentId || location.pathname}`;
    const last = storageGet(key);
    const chosen = chooseFresh(candidates, limit, last);

    candidates.forEach(card => {
      card.hidden = true;
      card.style.removeProperty('animation-delay');
    });
    chosen.forEach((card, index) => {
      grid.appendChild(card);
      card.hidden = false;
      card.style.animationDelay = `${index * 35}ms`;
    });
    grid.hidden = false;
    stripProductWorldControls(grid);
    const nextSignature = signature(chosen);
    if (nextSignature) storageSet(key, nextSignature);
    world.dataset.sqFreshMixReady = 'true';
  }

  function buildSmartSelection(root) {
    const cards = [...root.querySelectorAll('[data-sq-pick]')];
    const quotas = {
      same: Number(root.dataset.sameCount || 4),
      complement: Number(root.dataset.complementCount || 4),
      trending: Number(root.dataset.trendingCount || 4),
    };
    const total = Number(root.dataset.totalCount || (quotas.same + quotas.complement + quotas.trending));
    const currentId = clean(root.dataset.currentId);

    const selectOnce = () => {
      const pools = { same: [], complement: [], trending: [], fallback: [] };
      cards.forEach(card => (pools[card.dataset.source] || pools.fallback).push(card));
      Object.keys(pools).forEach(source => { pools[source] = shuffle(pools[source]); });

      const selectedBySource = { same: [], complement: [], trending: [] };
      const ids = new Set(currentId ? [currentId] : []);
      const families = new Set();
      const accepted = card => {
        const id = clean(card.dataset.productId) || productKey(card);
        const group = family(card.dataset.title || titleFor(card));
        if (!id || ids.has(id) || (group && families.has(group))) return false;
        ids.add(id);
        if (group) families.add(group);
        return true;
      };
      const take = (source, amount) => {
        for (const card of pools[source]) {
          if (selectedBySource[source].length >= amount) break;
          if (accepted(card)) selectedBySource[source].push(card);
        }
      };
      take('same', quotas.same);
      take('complement', quotas.complement);
      take('trending', quotas.trending);

      const selected = [];
      const max = Math.max(selectedBySource.same.length, selectedBySource.complement.length, selectedBySource.trending.length);
      for (let index = 0; index < max; index += 1) {
        if (selectedBySource.same[index]) selected.push(selectedBySource.same[index]);
        if (selectedBySource.complement[index]) selected.push(selectedBySource.complement[index]);
        if (selectedBySource.trending[index]) selected.push(selectedBySource.trending[index]);
      }

      if (selected.length < total) {
        for (const card of shuffle([...pools.same, ...pools.complement, ...pools.trending, ...pools.fallback])) {
          if (selected.length >= total) break;
          if (selected.includes(card)) continue;
          if (accepted(card)) selected.push(card);
        }
      }
      return selected.slice(0, total);
    };

    const storageKey = `sq:pdp:fresh:more-discover:${currentId || location.pathname}`;
    const previous = storageGet(storageKey);
    let selected = [];
    for (let attempt = 0; attempt < 7; attempt += 1) {
      selected = selectOnce();
      if (!previous || signature(selected) !== previous) break;
    }

    if (previous && signature(selected) === previous) {
      const previousSet = new Set(previous.split('|').filter(Boolean));
      const selectedSet = new Set(selected);
      const alternate = shuffle(cards).find(card => !selectedSet.has(card) && !previousSet.has(productKey(card)));
      if (alternate && selected.length) selected[selected.length - 1] = alternate;
    }
    return { selected, storageKey };
  }

  function mixSmartPicks(root, announce = false) {
    if (!(root instanceof Element)) return;
    const grid = root.querySelector('[data-sq-picks-grid]');
    if (!(grid instanceof HTMLElement)) return;
    const cards = [...grid.querySelectorAll('[data-sq-pick]')];
    if (!cards.length) return;

    const { selected, storageKey } = buildSmartSelection(root);
    cards.forEach(card => {
      card.hidden = true;
      card.style.animationDelay = '0ms';
    });
    selected.forEach((card, index) => {
      grid.appendChild(card);
      card.hidden = false;
      card.style.animationDelay = `${index * 35}ms`;
    });
    const nextSignature = signature(selected);
    if (nextSignature) storageSet(storageKey, nextSignature);

    const status = root.querySelector('[data-sq-picks-status]');
    if (status) status.textContent = announce
      ? 'A fresh recommendation mix is now showing.'
      : 'Fresh picks from this category, complementary pieces, and styles customers are loving now.';

    root.dataset.sqFreshMixReady = 'true';
    document.dispatchEvent(new CustomEvent('product-grid:updated', { detail: { source: 'sq-pdp-fresh-mix' } }));
  }

  function init(scope = document) {
    scope.querySelectorAll?.('[data-sq-product-world]').forEach(mixProductWorld);
    scope.querySelectorAll?.('[data-sq-smart-picks]').forEach(root => mixSmartPicks(root, false));
  }

  function boot() {
    init(document);
    requestAnimationFrame(() => init(document));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', event => {
    requestAnimationFrame(() => init(event.target));
  });

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const refresh = target?.closest('[data-sq-picks-refresh]');
    if (!refresh) return;
    const root = refresh.closest('[data-sq-smart-picks]');
    if (!root) return;
    requestAnimationFrame(() => requestAnimationFrame(() => mixSmartPicks(root, true)));
  }, true);
})();
